import { type api, useApiQuery, useDashboard, useTeam } from '@vrplatform/data';
import { useOwnerStatementSummariesDetailFilter } from './useOwnerStatementSummariesDetailFilter';
import type { Dashboard } from '@vrplatform/ui-common';
import { day } from '@vrplatform/utils';
import { useSessionStorage } from '@mantine/hooks';

export const STORYBOOK_OWNER_STATEMENT_SUMMARIES_DETAIL_QUERY_KEY =
  'owner-statement-summaries-detail-report';

export const getOwnerStatementSummariesDetailQueryFilterParams = ({
  filter,
  dashboard,
}: {
  filter: ReturnType<typeof useOwnerStatementSummariesDetailFilter>['filter'];
  dashboard: Dashboard;
}): api['/reports/owner-statement-summaries-per-layout/details/{contactId}']['get']['parameters'] => {
  const [startAt, end] = filter.monthRange.split('...');

  const endAt = day(end).add(1, 'day').yyyymmdd();

  return {
    query: {
      startAt,
      endAt,
      view: filter.view,
      listingIds: filter.listingIds?.join(','),
      viewAs: dashboard === 'owner' ? 'owner' : 'manager',
    },
    path: {
      contactId: filter.contactId!,
    },
  };
};

export type OwnerStatementSummariesDetailData = NonNullable<
  ReturnType<typeof useOwnerStatementSummariesDetailQuery>['data']
>;

export type OwnerStatementSummariesDetailTableSection = NonNullable<
  ReturnType<typeof useOwnerStatementSummariesDetailQuery>['data']
>['layouts'][0]['otherSections'][0];

export type OwnerStatementSummariesDetailTableRow =
  OwnerStatementSummariesDetailTableSection['rows'][0];

export const useOwnerStatementSummariesDetailQuery = () => {
  const [{ id: teamId }] = useTeam();
  const { filter } = useOwnerStatementSummariesDetailFilter();
  const [dashboard] = useDashboard();

  const [savedLayoutId, setLayoutId] = useSessionStorage({
    key: `${filter.contactId || 'unassigned'}-${filter.monthRange}-${filter.view}-${filter.listingIds?.join(',')}-${filter.view}`,
  });

  const queryData = useApiQuery(
    async (api, signal, args) => {
      if (!args.filter.contactId) throw new Error('Contact is required');

      const [team, contact, report, listing] = await Promise.all([
        api
          .GET('/teams/{id}', {
            params: {
              path: {
                id: args.teamId,
              },
            },
            signal,
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data;
          }),
        api
          .GET('/contacts/{id}', {
            params: {
              path: {
                id: args.filter.contactId,
              },
            },
            signal,
          })
          .then((res) => {
            if (res.error) throw res.error;
            return res.data;
          }),

        api
          .GET(
            '/reports/owner-statement-summaries-per-layout/details/{contactId}',
            {
              params: getOwnerStatementSummariesDetailQueryFilterParams({
                filter: args.filter,
                dashboard,
              }),
              signal,
            }
          )
          .then((res) => {
            if (res.error) {
              const message = (res.error.message || '').toLowerCase();
              const hasNoJournalEntries = message.includes(
                'no journal entries found'
              );

              // PRO-12873: this should render the existing empty placeholder
              // state, not surface as an API query error notification.
              if (res.error.code === 'NOT_FOUND' || hasNoJournalEntries)
                return null;

              throw res.error;
            }
            return res.data;
          }),
        filter.listingIds?.length === 1 && filter.listingIds[0]
          ? api
              .GET('/listings/{id}', {
                params: {
                  path: {
                    id: args.filter.listingIds![0],
                  },
                },
                signal,
              })
              .then((res) => {
                if (res.error) throw res.error;
                return res.data;
              })
          : null,
      ]);

      if (!report) return null;

      return {
        ...report,
        team,
        contact,
        listing,
        resolvedView: args.filter.view,
      };
    },
    {
      variables: {
        filter,
        teamId,
      },
      storybookQueryKey: STORYBOOK_OWNER_STATEMENT_SUMMARIES_DETAIL_QUERY_KEY,
      queryGroup: 'owner-statements',
      uniqueQueryKey: 'owner-statement-summaries-detail-report',
      queryKeys: ['journalEntries', 'reports'],
      keepPreviousData: true,
    }
  );

  const layoutIds =
    queryData.data?.layouts?.map((layout) => layout.layoutId) || [];
  const isLayoutIdValid = layoutIds.includes(savedLayoutId);
  const firstLayoutId = queryData.data?.layouts?.[0]?.layoutId;
  const layoutId = isLayoutIdValid
    ? savedLayoutId || firstLayoutId
    : firstLayoutId;

  return {
    ...queryData,
    layoutId,
    setLayoutId,
  };
};
